import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useProvider, Toast } from '../context/MenuProvider';
import Markdown from '../components/Utils/Markdown';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Card, Alert, Col, Row, InputGroup, Tab, Nav, Container} from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Swal from 'sweetalert2';

export default function GaleriaApresentacao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [editando, setEditando] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [apresentacao, setApresentacao] = useState(null);
  const [imagens, setImagens] = useState([]);
  const [modoApresentacao, setModoApresentacao] = useState(false);
  const [slideAtual, setSlideAtual] = useState(0);
  const [processo, setProcesso] = useState({ feitos: 0, total: 0 });
  const containerRef = useRef(null);
  const {
    crud,
    buscarDadosPorId,
    salvarDados,
    setLinks,
    setBtns,
    hasPerms,
  } = useProvider();
  const { post, del, get, loading, patch } = crud || {};
  
  useEffect(() => {
    const excluirApresentacao = async () => {
      await Swal.fire({
        title: "Exluir esta apresentação?",
        text: "Excluir a apresentação definitivo, esta ação não pode ser revertida.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Sim, excluir!"
      }).then(async(result) => {
        if (result.isConfirmed) {
          try{
            await del(`/rede/apresentacoes/${id}/`);
            const novaLista = imagens.filter(img => img.id !== id);
            salvarOrdem(novaLista);
            Toast.fire({
                icon: "success",
                title: "Apresentação excluída com sucesso"
            });
            navigate(`/painel/apresentacoes`,{replace:true})
          }catch(err){
           // alert(JSON.stringify(err.response))
            Swal.fire({
              title: `Erro ${err.status || err.code}`,
              text: err.response?.data?.detail || 'Esta apresentação não pode ser excluída',
              icon: "error"
            });
          }finally{
          }
        }
      });
    }
    
    setLinks([
      ...(hasPerms(["rede.add_apresentacao"]) ? [
        { name: 'Novo', to: '/painel/slide' },
      ] : []),
      ...(hasPerms(["rede.view_apresentacao"]) ? [
        { name: 'Todas as apresentações', to: '/painel/apresentacoes' },
      ] : []),
      ...((id && hasPerms(["rede.delete_apresentacao"])) ? [{ name: 'Excluir', onClick: () => excluirApresentacao() }] : []),
    ]);
  
    setBtns([
      ...((editando && hasPerms(["rede.change_apresentacao"])) ? [
        { icon: 'bi-check', onClick: () => handleSubmit() }
      ] : []),
      ...((id && !editando && hasPerms(["rede.delete_apresentacao"])) ? [
        { icon: 'bi-trash', onClick: () => excluirApresentacao() }
      ] : []),
    ]);
  
    return () => {
      setLinks([]);
      setBtns([]);
    };
  }, [setLinks, setBtns, id, apresentacao, editando, hasPerms]);

  // Carregar ordem salva
  useEffect(() => {
    const buscarLocal = async()=>{
      try{
        const response = await buscarDadosPorId("db_apresentacoes", id);
        //alert(JSON.stringify(response))
        if(response){
          setApresentacao(response);
          setImagens(response.imagens || []);
        }
      }catch(err){
        
      }finally{
        buscarServer();
      }
    }
    
    const buscarServer = async()=>{
      try {
        const response = await get(`/rede/apresentacoes/${id}/?expand=imagens`);
        if(response && response.length){
          setApresentacao(response[0])
          setImagens(response[0].imagens);
          //alert(JSON.stringify(response[0]))
          await salvarDados("db_apresentacoes", {
            ...response[0],
            id:id
          });
        }else{
          setImagens([]); // fallback
        }
      } catch(err){
        
      }
    }
    if(id && hasPerms("rede.view_apresentacao")){
      buscarLocal()
    }
  }, [id]);

  const salvarOrdem = async(lista) => {
    setImagens(lista);
    try{
      await salvarDados("db_apresentacoes", {
        ...apresentacao,
        imagens:lista,
        id:id
      });
      if (lista.length){
        await post(
          {
            apresentacao:id,
            imagens:lista
          },
          `/rede/imagens-das-apresentacoes/ordenar/`,
          null,
          'slide_page'
        )
      }
    }catch(err){
      
    }
  };

  // Drag & Drop
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const novaOrdem = Array.from(imagens);
    const [removido] = novaOrdem.splice(result.source.index, 1);
    novaOrdem.splice(result.destination.index, 0, removido);
    salvarOrdem(novaOrdem);
  };

  // ---- Upload e compressão WebP sem cortar ----
  const handleUpload = (e) => {
    const arquivos = Array.from(e.target.files);
    setProcesso({ feitos: 0, total: arquivos.length * 2});
    const lista = [...imagens];
    
    arquivos.forEach((file) => {
      if (!file.type.startsWith('image/')) return;
  
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
  
          const MAX_WIDTH = 1600;
          const MAX_HEIGHT = 1200;
          let width = img.width;
          let height = img.height;
  
          const scale = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height, 1);
          width *= scale;
          height *= scale;
  
          canvas.width = width;
          canvas.height = height;
  
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
  
          canvas.toBlob(
            async (blob) => {
              if (!blob) return;
  
              // Cria File com o mesmo nome original, mas extensão .webp
              const fileName = file.name.replace(/\.[^/.]+$/, ".webp");
              const webpFile = new File([blob], fileName, { type: "image/webp" });
              setProcesso(prev => {
                  const novosFeitos = prev.feitos + 1;
                  return { ...prev, feitos: novosFeitos };
              });
  
              const formData = new FormData();
              formData.append("arquivo", webpFile); // nome do campo igual ao ImageField no Django
              formData.append("nome", fileName);
              formData.append("apresentacao", id);
  
              try {
                const response = await post(
                  formData,
                  "/rede/imagens-das-apresentacoes/",
                  "multipart/form-data"
                );
                if (response && response.length) {
                  lista.push(response[0]);
                  setImagens(lista);
                  salvarOrdem(lista);
                }
              } catch (err) {
                //alert(JSON.stringify(err.response || err));
                console.error("Erro ao enviar imagem:", err);
              } finally {
                setProcesso(prev => {
                  const novosFeitos = prev.feitos + 1;
              
                  if (novosFeitos === prev.total) {
                    // terminou tudo → reset
                    return { feitos: 0, total: 0 };
                  }
              
                  return { ...prev, feitos: novosFeitos };
                });
              }
            },
            "image/webp",
            0.8
          );
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // ---- Apresentação ----
  const iniciarApresentacao = (index = 0) => {
    const imagensAtivas = imagens.filter(img => !img.ignorar);
    if (imagensAtivas.length === 0) return;

    // Ajusta slideAtual para a posição correta entre imagens ativas
    let idx = imagensAtivas.findIndex(img => img.id === imagens[index].id);
    if (idx === -1) idx = 0;

    setSlideAtual(idx);
    setModoApresentacao(true);

    setTimeout(() => {
      if (containerRef.current) {
        if (containerRef.current.requestFullscreen) containerRef.current.requestFullscreen();
        else if (containerRef.current.webkitRequestFullscreen) containerRef.current.webkitRequestFullscreen();
        else if (containerRef.current.msRequestFullscreen) containerRef.current.msRequestFullscreen();
      }
    }, 50);
  };

  const encerrarApresentacao = () => {
    setModoApresentacao(false);
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    }
  };

  const proxima = () => {
    const imagensAtivas = imagens.filter(img => !img.ignorar);
    setSlideAtual(i => (i + 1) % imagensAtivas.length);
  };

  const anterior = () => {
    const imagensAtivas = imagens.filter(img => !img.ignorar);
    setSlideAtual(i => (i - 1 + imagensAtivas.length) % imagensAtivas.length);
  };

  const excluirImagem = async(id) => {
    await Swal.fire({
      title: "Exluir esta imagem?",
      text: "Excluir a imagem definitivo, esta ação não pode ser revertida.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, excluir!"
    }).then(async(result) => {
      if (result.isConfirmed) {
        try{
          await del(`/rede/imagens-das-apresentacoes/${id}/`);
          const novaLista = imagens.filter(img => img.id !== id);
          salvarOrdem(novaLista);
          Toast.fire({
              icon: "success",
              title: "Imagem excluída com sucesso"
          });
        }catch(err){
         // alert(JSON.stringify(err.response))
          Swal.fire({
            title: `Erro ${err.status || err.code}`,
            text: err.response?.data?.detail || 'Esta imagem não pode ser excluída',
            icon: "error"
          });
        }finally{
        }
      }
    });
  };

  const toggleIgnorar = async (id) => {
    const imagem = imagens.find(img => img.id === id);
  
    if (!imagem) return;
  
    const novaLista = imagens.map(img =>
      img.id === id ? { ...img, ignorar: !img.ignorar } : img
    );
    try{
      salvarOrdem(novaLista);
      await patch(
        { ignorar: !imagem.ignorar },
        `/rede/imagens-das-apresentacoes/${id}/`,
      );
    }catch(err){
      
    }
  };

  // ---- Teclas e clique ----
  useEffect(() => {
    if (!modoApresentacao) return;
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown") proxima();
      if (e.key === "ArrowLeft" || e.key === "PageUp") anterior();
      if (e.key === "Escape") encerrarApresentacao();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modoApresentacao, imagens]);

  const handleClick = (e) => {
    const largura = window.innerWidth;
    if (e.clientX < largura / 2) anterior();
    else proxima();
  };

  // ---- Detecta saída do fullscreen ----
  useEffect(() => {
    const handleFsChange = () => {
      if (!document.fullscreenElement) setModoApresentacao(false);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    document.addEventListener("msfullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
      document.removeEventListener("msfullscreenchange", handleFsChange);
    };
  }, []);

  const imagensAtivas = imagens.filter(img => !img.ignorar);
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errors = {};
    if (!apresentacao?.nome?.trim()) {
      errors.nome = 'Por favor, informe o título da apresentação.';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    try{
      const formData = new FormData();
      formData.append("nome", apresentacao?.nome); 
      formData.append("descricao", apresentacao?.descricao); 
      const apiRequest = id ? patch : post;
      const response = await apiRequest(
        formData,
        `/rede/apresentacoes/${id ? `${id}/` : ''}`,
        `multipart/form-data`,
        `apresentacoes`
      );
      if(response && response.length){
        setEditando(false);
        navigate(`/painel/slide/${response[0].id}`, {replace:true})
      }
    }catch (err){
      //alert(JSON.stringify(err.response))
      const errorMessage =
        typeof err.response?.data === 'string'
          ? 'Erro ao salvar o cliente.'
          : err.response?.data?.detail;
      setFormErrors(prev => ({
        ...prev,
        apiError: errorMessage,
        nome: err.response?.data?.nome,
        descricao: err.response?.data?.descricao,
      }));
    }
  }

  return (
    <div>
      {!modoApresentacao && (
        <>
          {((editando && hasPerms("rede.change_apresentacao")) || (!id && hasPerms("rede.add_apresentacao")))
            ? (<>
              <Form onSubmit={handleSubmit}>
                {formErrors.apiError && <Alert variant="danger">{formErrors.apiError}</Alert>}
                <Form.Group controlId="formNome" className="mt-3">
                  <Form.Label>Título da apresentação</Form.Label>
                  <InputGroup>
                    <Form.Control 
                      type="text" 
                      value={apresentacao?.nome} 
                      onChange={(e) => setApresentacao((prev)=>({
                        ...prev,
                        nome:e.target.value
                      }))} 
                    />
                  </InputGroup>
                  {formErrors.nome && <Alert variant="danger">{formErrors.nome}</Alert>}
                </Form.Group>
          
                <Form.Group controlId="formObservacoes" className="mt-3">
                  <Form.Label>Observações</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    value={apresentacao?.descricao} 
                    onChange={(e) => setApresentacao((prev)=>({
                      ...prev,
                      descricao:e.target.value
                    }))} 
                    rows={5} 
                  />
                  {formErrors.descricao && <Alert variant="danger">{formErrors.descricao}</Alert>}
                </Form.Group>
          
                <Button variant="secondary" type="submit" className="mt-4 w-100" disabled={loading}>
                  Salvar
                </Button>
            </Form>
            </>)
            : (<>
                <h2>{apresentacao?.nome || `Título da apresentação`}</h2>
                <div className="text-muted">
                  <Markdown texto={apresentacao?.descricao || ``} />
                </div>
            </>)}
          
          {((id && hasPerms("rede.change_apresentacao")) && (!editando && hasPerms("rede.add_apresentacao"))) && (<>
            {hasPerms("rede.add_imagemapresentacao") && <Button onClick={()=>inputRef.current.click()} disabled={processo.total > 0} variant="secondary">Add imagens</Button>}
            <Button onClick={()=>setEditando(true)} disabled={processo.total > 0} className="ms-1" variant="warning"><i className="bx bx-edit"></i></Button>
            <input type="file" accept="image/*" multiple onChange={handleUpload} ref={inputRef} className="d-none"/>
            {processo.total > 0 && (
              <div className="mt-2">
                <ProgressBar
                  now={(processo.feitos / processo.total) * 100}
                  label={`${parseInt((processo.feitos / processo.total) * 100)}%`}
                  animated
                  striped
                />
              </div>
            )}
            {imagens?.length > 0 && (<>
              {(!processo.total && hasPerms("rede.view_imagemapresentacao")) && (
                <Button onClick={() => iniciarApresentacao(0)} style={{ marginLeft: 10 }}>
                  Iniciar apresentação
                </Button>
              )}
              <div className="pt-2">
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="lista-imagens">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {imagens.map((img, index) => (
                          <Draggable key={img.id} draggableId={String(img.id)} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  marginBottom: 10,
                                  background: "#eee",
                                  padding: 10,
                                  //display: "flex",
                                  alignItems: "center",
                                  //gap: 10,
                                  //justifyContent: "space-between",
                                  ...provided.draggableProps.style,
                                }}
                              >
                                <Row className="align-items-center">
                                  <Col xs="auto">
                                    <img 
                                      src={img.arquivo} 
                                      alt={img.nome} 
                                      style={{ height: 60 }} 
                                    />
                                  </Col>
                                  <Col>
                                    <InputGroup>
                                      {hasPerms("rede.view_imagemapresentacao") && (<Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => iniciarApresentacao(index)}
                                      >
                                        ▶
                                      </Button>)}
                                      {hasPerms("rede.change_imagemapresentacao") && (<>
                                        <Form.Group controlId={index} className="mx-2">
                                          <Form.Check
                                            type="switch"
                                            label="Ignorar"
                                            checked={img.ignorar}
                                            onChange={() => toggleIgnorar(img.id)}
                                          />
                                        </Form.Group>
                                      </>)}
                                      {hasPerms("rede.delete_imagemapresentacao") && (<>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={() => excluirImagem(img.id)}
                                        >
                                          Excluir
                                        </Button>
                                      </>)}
                                    </InputGroup>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </>)}
          </>)}
        </>
      )}

      {/* Fullscreen apenas imagem */}
      {modoApresentacao && (
        <div
          ref={containerRef}
          onClick={handleClick}
          style={{
            background: "black",
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {imagensAtivas[slideAtual] && (
            <img
              src={imagensAtivas[slideAtual].arquivo}
              alt=""
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          )}
        </div>
      )}
    </div>
  );
}